import React from "react";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { AfterLoginTemplate } from "src/components/AfterLoginTemplate";
import { Spinner } from "src/components/Spinner";
import { LinkWithIcon } from "src/components/links/LinkWithIcon";
import { routes } from "src/constants/routes";
import { IReport } from "src/models/report";
import { IAppState } from "src/models/state";
import { IAsyncState } from "src/state/action";
import {
  downloadReport,
  loadReportsList,
  removeMessage,
  removeReport,
} from "src/state/manager/actions";
import "src/style/containers/reportManager.scss";
import { ReportsTable } from "./ReportsTable";

// #region -------------- Interfaces -------------------------------------------------------------------

interface IStateProps {
  reports: IAsyncState<IReport[]>;
  reportRemoveStatus: IAsyncState<boolean>;
  reportDownloadStatus: IAsyncState<boolean>;
}

interface IDispatchProps {
  onLoadReportsList(): void;
  onReportSelected(reportID: string): void;
  onPreviewReport(reportID: string): void;
  onDownloadReport(reportID: string): void;
  onRemoveReport(reportID: string): void;
  onRemoveMessage(): void;
}

interface IOwnProps {}

interface IProps extends IStateProps, IDispatchProps, IOwnProps {}

// #endregion

// #region -------------- Component -------------------------------------------------------------------

class ReportManager extends React.Component<IProps> {
  private successMessageTimeout: NodeJS.Timeout;

  public componentDidMount() {
    this.props.onLoadReportsList();
  }

  public componentWillUnmount() {
    clearTimeout(this.successMessageTimeout);
  }

  public render() {
    return (
      <AfterLoginTemplate>
        <div className="rb-report-manager-container">
          <div className="rb-content">
            <div className="container">
              {/* <h1 className="rb-title-dark">Supported databases</h1>
              <div className="rb-description-dark">
                Get a Data API on top of these supported databases:
                <SupportedDatabases />
                Want to learn more? Check{" "}
                <a href="https://peekdata.io/developers/index.html">
                  documentation.
                </a>
              </div> */}
              <h1 className="rb-title-dark">Report Builder Component</h1>
              <div className="rb-description-dark">
                With our paid plans we ship a{" "}
                <a
                  target="_blank"
                  href=" https://gitlab.com/peekdata/react-report-builder-example"
                >
                  Report Builder Component
                </a>{" "}
                that you can use to browse and visualize your data. You can also
                get example SQL or JSON queries to later use them in the code.
                Here are some example reports built using our{" "}
                <a target="_blank" href="https://peekdata.io/features/">
                  Data API
                </a>
                :
              </div>
              {this.renderReportsTable()}
              <div className="rb-btn-container" style={{ marginTop: 25 }}>
                <LinkWithIcon
                  linkTo={routes.builder}
                  title="Try out Report Builder"
                  iconClasses="fas fa-plus-circle"
                  styleClasses="rb-btn-small rb-btn-crimson"
                />
              </div>
              <h1 className="rb-title-dark" style={{ marginTop: 50 }}>
                Other libraries
              </h1>
              <div className="rb-description-dark">
                We've also prepared some examples of how to use our Data API
                with other popular data visualization libraries:{" "}
                <a href="/examples/bizcharts">BizCharts</a> and{" "}
                <a href="/examples/chartjs">Chart.js</a>.
              </div>
            </div>
          </div>
        </div>
      </AfterLoginTemplate>
    );
  }

  private renderReportsTable = () => {
    const { reports, reportRemoveStatus, reportDownloadStatus } = this.props;

    if (reports && reports.isFetching) {
      return <Spinner />;
    }

    if (reports && reports.error) {
      return <div className="alert alert-danger">{reports.error}</div>;
    }

    return (
      <ReportsTable
        data={reports && reports.data}
        reportDownloadStatus={reportDownloadStatus}
        reportRemoveStatus={reportRemoveStatus}
        onSelect={this.onSelectReport}
        onPreview={this.onPreviewReport}
        onDownload={this.onDownloadReport}
        onRemove={this.onRemoveReport}
      />
    );
  };

  private onSelectReport = (reportID) => {
    this.props.onReportSelected(reportID);
  };

  private onPreviewReport = (reportID) => {
    this.props.onPreviewReport(reportID);
  };

  private onDownloadReport = (reportID) => {
    this.props.onDownloadReport(reportID);
  };

  private onRemoveReport = (reportID) => {
    clearTimeout(this.successMessageTimeout);

    this.props.onRemoveReport(reportID);

    this.successMessageTimeout = setTimeout(() => {
      this.props.onRemoveMessage();
    }, 3000);
  };
}

// #endregion

// #region -------------- Connect -------------------------------------------------------------------

const connected = connect<IStateProps, IDispatchProps, IOwnProps, IAppState>(
  (state) => {
    const { reports, reportRemoveStatus, reportDownloadStatus } =
      state.reportManager;

    return {
      reports,
      reportRemoveStatus,
      reportDownloadStatus,
    };
  },
  (dispatch) => {
    return {
      onLoadReportsList: () => dispatch(loadReportsList()),
      onReportSelected: (reportID: string) =>
        dispatch(push(`${routes.report}/${reportID}`)),
      onPreviewReport: (reportID: string) =>
        dispatch(push(`${routes.report}/${reportID}?report-type=0`)),
      onDownloadReport: (reportID: string) =>
        dispatch(downloadReport(reportID)),
      onRemoveReport: (reportID: string) => dispatch(removeReport(reportID)),
      onRemoveMessage: () => dispatch(removeMessage()),
    };
  }
)(ReportManager);

export { connected as ReportManager };

// #endregion
